import React, { useState } from 'react';
import { ChevronDown, Book, Zap, Shield, Coins, Code, Link2, Users } from 'lucide-react';

interface DocSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const Docs: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>('getting-started');

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const docSections: DocSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Zap size={24} />,
      content: (
        <div className="doc-content">
          <h4>Welcome to BLOOT</h4>
          <p>
            BLOOT is a permissionless loot crate protocol built on Solana. Create, share, and monetize 
            provably fair lootbox games instantly via Solana Blinks (Blockchain Links).
          </p>
          
          <h4>Quick Start</h4>
          <ol>
            <li>Navigate to the <strong>CREATE</strong> tab</li>
            <li>Enter a unique name for your Bloot Box (case names are case-insensitive)</li>
            <li>Configure your drop table with win tiers and chances</li>
            <li>Optionally set up a localized jackpot to BLOOT things up!</li>
            <li>Connect your Solana wallet</li>
            <li>Click <strong>BLOOT ME UP</strong> to create your Blink</li>
            <li>Share the link and start earning!</li>
          </ol>

          <h4>Requirements</h4>
          <ul>
            <li>A Solana wallet (Phantom, Solflare, etc.)</li>
            <li><strong>Phantom users:</strong> Enable Blinks in Experimental Features within your Phantom wallet settings</li>
            <li>No coding knowledge required</li>
            <li>No contract deployment needed</li>
          </ul>
        </div>
      )
    },
    {
      id: 'how-it-works',
      title: 'How It Works',
      icon: <Book size={24} />,
      content: (
        <div className="doc-content">
          <h4>Architecture</h4>
          <p>
            BLOOT leverages Solana's high-performance blockchain and the Blinks protocol to create 
            instant, shareable loot crate experiences. Each crate is backed by on-chain randomness 
            for provable fairness.
          </p>

          <h4>The Blink Protocol</h4>
          <p>
            Solana Blinks (Blockchain Links) allow any URL to unfurl into an interactive blockchain 
            interface. When users click your Blink, they can play your loot crate directly from 
            social media platforms like X (Twitter) without leaving the app.
          </p>

          <h4>Opening Cases</h4>
          <p>
            When a user opens a Bloot Box, they pay the entry cost in $BLOOT tokens. The outcome is 
            determined using on-chain randomness based on the crate's drop table. Each tier in the 
            drop table has a specific chance percentage and payout amount.
          </p>
          <ul>
            <li>User pays the entry cost (auto-calculated from drop table odds)</li>
            <li>On-chain randomness determines the winning tier</li>
            <li>User receives $BLOOT tokens based on the winning tier's payout</li>
            <li>All transactions are recorded on-chain and can be verified</li>
          </ul>

          <h4>Jackpots</h4>
          <p>
            Creators can optionally set up a localized jackpot tier in their drop table. The jackpot 
            starts at a base amount and accumulates with each play (1% of entry cost is added to the 
            jackpot pool). When a player wins the jackpot tier, they receive the full accumulated 
            jackpot amount, and it resets to the base amount.
          </p>
          <ul>
            <li>Jackpot starts at the base amount set by the creator</li>
            <li>1% of each entry cost is added to the jackpot pool</li>
            <li>When won, the player receives the full accumulated jackpot</li>
            <li>The jackpot resets to the base amount after a win</li>
            <li>Jackpot wins are automatically detected and tracked</li>
          </ul>

          <h4>Revenue Model</h4>
          <ul>
            <li><strong>Entry Cost:</strong> Automatically calculated based on your drop table odds and RTP</li>
            <li><strong>Platform Fee:</strong> 1% goes to BLOOT protocol</li>
            <li><strong>Jackpot Fee:</strong> 1% (only if jackpot tier is enabled)</li>
            <li><strong>Creator Earnings:</strong> 1% of total volume (entry cost per play) goes to you</li>
            <li><strong>Affiliate Earnings:</strong> 1% of entry cost per play for referrals</li>
          </ul>
        </div>
      )
    },
    {
      id: 'provable-fairness',
      title: 'Provable Fairness',
      icon: <Shield size={24} />,
      content: (
        <div className="doc-content">
          <h4>On-Chain Randomness</h4>
          <p>
            All outcomes are determined using verifiable random functions (VRF) on Solana. 
            Every spin result can be independently verified on-chain, ensuring complete transparency.
          </p>

          <h4>Verification</h4>
          <p>
            Each loot crate outcome includes a transaction hash that you can verify on Solana 
            explorers. The randomness seed is generated from:
          </p>
          <ul>
            <li>Block hash of the transaction</li>
            <li>User's wallet signature</li>
            <li>Timestamp of the spin</li>
            <li>Previous block data</li>
          </ul>

          <h4>No Manipulation</h4>
          <p>
            Since randomness is generated on-chain, neither creators nor the platform can 
            manipulate outcomes. Every spin is truly random and fair.
          </p>
        </div>
      )
    },
    {
      id: 'earnings',
      title: 'Earnings & Fees',
      icon: <Coins size={24} />,
      content: (
        <div className="doc-content">
          <h4>Creator Revenue</h4>
          <p>
            As a creator, you earn <strong>1% of total volume</strong> (entry cost per play) from every play on your crate. 
            Earnings are tracked per case and can be viewed in the <strong>REWARDS</strong> tab.
          </p>

          <h4>Affiliate Program</h4>
          <p>
            Share your Blink with an affiliate parameter to earn <strong>1% of entry cost per play</strong> for each referral. 
            Affiliate earnings are tracked separately per case and can be viewed in the <strong>REWARDS</strong> tab.
          </p>

          <h4>Fee Breakdown</h4>
          <div className="fee-table">
            <div className="fee-row">
              <span>Entry Cost (per play)</span>
              <span>Auto-calculated from drop table</span>
            </div>
            <div className="fee-row">
              <span>Platform Fee</span>
              <span className="fee-deduction">1%</span>
            </div>
            <div className="fee-row">
              <span>Creator Earnings</span>
              <span className="highlight-text">1%</span>
            </div>
            <div className="fee-row">
              <span>Affiliate Earnings</span>
              <span className="highlight-text">1%</span>
            </div>
            <div className="fee-row">
              <span>Jackpot (optional)</span>
              <span>1% (if enabled)</span>
            </div>
          </div>

          <h4>Tracking Earnings</h4>
          <p>
            View your total earnings, creator earnings per case, and affiliate earnings in the <strong>REWARDS</strong> tab. 
            All transactions are recorded on-chain and can be verified via Solana explorers.
          </p>
          
          <h4>Play History</h4>
          <p>
            View your complete play history in the <strong>HISTORY</strong> tab. You can close settled plays to reclaim rent 
            (gas refund). The "Refund All Gas" button processes up to 20 plays at a time - click multiple times if you have more.
          </p>

          <h4>Withdrawals</h4>
          <p>
            Earnings accumulate in your wallet automatically. There's no need to manually claim - 
            funds are sent directly to your specified address after each spin.
          </p>
        </div>
      )
    },
    {
      id: 'blinks',
      title: 'Using Blinks',
      icon: <Link2 size={24} />,
      content: (
        <div className="doc-content">
          <h4>What Are Blinks?</h4>
          <p>
            Solana Blinks are special URLs that unfurl into interactive blockchain interfaces. 
            They work on any platform that supports link previews, including X (Twitter), Discord, 
            Telegram, and more.
          </p>

          <h4>Sharing Your Blink</h4>
          <p>
            Once you create a Blootbox, you'll receive a unique Blink URL. Simply copy and paste 
            this link anywhere you want to share your crate:
          </p>
          <ul>
            <li>X (Twitter) posts and replies</li>
            <li>Discord channels and DMs</li>
            <li>Telegram groups</li>
            <li>Email campaigns</li>
            <li>Your website or blog</li>
          </ul>

          <h4>User Experience</h4>
          <p>
            When users click your Blink, they'll see a rich preview card with your crate name and 
            details. They can play directly from the preview without leaving the platform. 
            No app downloads or wallet connections required upfront.
          </p>

          <h4>Best Practices</h4>
          <ul>
            <li>Use descriptive crate names that attract attention</li>
            <li>Share during peak engagement times</li>
            <li>Engage with players in comments</li>
            <li>Create themed crates for events or communities</li>
          </ul>
        </div>
      )
    },
    {
      id: 'api',
      title: 'API & Integration',
      icon: <Code size={24} />,
      content: (
        <div className="doc-content">
          <h4>REST API</h4>
          <p>
            BLOOT provides a RESTful API for developers who want to integrate loot crates into 
            their applications or build custom interfaces.
          </p>

          <h4>Endpoints</h4>
          <div className="code-block">
            <pre>{`POST /api/v1/blinks/create
{
  "name": "My Crate",
  "walletAddress": "your-solana-address"
}

GET /api/v1/blinks/{blinkId}/stats
GET /api/v1/blinks/{blinkId}/plays
GET /api/v1/user/{address}/earnings`}</pre>
          </div>

          <h4>Webhooks</h4>
          <p>
            Subscribe to webhooks for real-time notifications about plays, earnings, and crate events. 
            Configure webhook URLs in your dashboard.
          </p>

          <h4>SDKs</h4>
          <p>
            Official SDKs are available for JavaScript/TypeScript, Python, and Rust. 
            Check our GitHub repository for examples and documentation.
          </p>
        </div>
      )
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: <Users size={24} />,
      content: (
        <div className="doc-content">
          <h4>Frequently Asked Questions</h4>

          <div className="faq-item">
            <strong>Q: Do I need to deploy a smart contract?</strong>
            <p>A: No! BLOOT handles all contract deployment automatically. Just create and share.</p>
          </div>

          <div className="faq-item">
            <strong>Q: How do I know the games are fair?</strong>
            <p>A: All randomness is generated on-chain using verifiable random functions. You can verify any outcome on Solana explorers.</p>
          </div>

          <div className="faq-item">
            <strong>Q: When do I receive my earnings?</strong>
            <p>A: Earnings are sent automatically to your wallet after each spin. No manual claiming required.</p>
          </div>

          <div className="faq-item">
            <strong>Q: Can I pause or delete my crate?</strong>
            <p>A: No, crates cannot be paused or deleted once created. Make sure you're ready before deploying your Blink.</p>
          </div>

          <div className="faq-item">
            <strong>Q: What happens if someone wins?</strong>
            <p>A: Winners receive $BLOOT tokens directly to their wallet based on the crate's drop table. Jackpot wins are automatically detected and tracked.</p>
          </div>

          <div className="faq-item">
            <strong>Q: Are there any limits?</strong>
            <p>A: No limits on the number of crates you can create. Entry costs are set by the creator in $BLOOT tokens. Case names must be unique (case-insensitive).</p>
          </div>

          <div className="faq-item">
            <strong>Q: How do jackpots work?</strong>
            <p>A: Creators can optionally set up a localized jackpot tier. The jackpot starts at a base amount and accumulates with each play. When won, it resets to the base amount.</p>
          </div>

          <div className="faq-item">
            <strong>Q: Can I track my earnings?</strong>
            <p>A: Yes! The REWARDS page shows your creator earnings (1% of total volume per case) and affiliate earnings (1% of entry cost per play per case).</p>
          </div>

          <div className="faq-item">
            <strong>Q: How do I refund gas from closed plays?</strong>
            <p>A: In the HISTORY tab, you can close individual plays or use "Refund All Gas" to close up to 20 plays at once. If you have more than 20, click the button multiple times.</p>
          </div>

          <div className="faq-item">
            <strong>Q: How do Blinks work on mobile?</strong>
            <p>A: Blinks work seamlessly on mobile devices. Users can play directly from social media apps without additional downloads.</p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="docs-container">
      <div className="section-header">
        <h3>Documentation</h3>
        <p className="section-desc">Everything you need to know about BLOOT.</p>
      </div>

      <div className="docs-sections">
        {docSections.map((section) => (
          <div key={section.id} className="doc-section">
            <button
              className="doc-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <div className="doc-section-title">
                <span className="doc-icon">{section.icon}</span>
                <span>{section.title}</span>
              </div>
              <ChevronDown
                size={20}
                className={`chevron ${expandedSection === section.id ? 'expanded' : ''}`}
              />
            </button>
            
            {expandedSection === section.id && (
              <div className="doc-section-content">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Docs;

